/**
 * The mesh's numbers and device posture in words, for the Mesh and Workbench pages (mesh round 3).
 */

import type { DevicePosture, FleetDevice, MeshNode, MeshPostureRequirement, MeshRelayLimits } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';

const DAY = 86_400_000;

/** Why a node is off the mesh, in words. */
export const OFF_REASON: Record<NonNullable<MeshNode['disabledReason']>, string> = { admin: 'by an administrator', 'device-revoked': 'device revoked', 'user-removed': 'its owner was removed', 'node-refused': 'node refused', 'key-expired': 'key expired', 'plan-lapsed': 'the team is no longer on Teams' };

/** "3 October 2026". */
const longDay = (iso: string) => dateLong(iso);

/** Why a node is off the mesh, as a sentence: "Off the mesh since the team left Teams on 3 October 2026", "Off the mesh: key expired". */
export function offTheMeshInWords(n: Pick<MeshNode, 'disabledReason'>, lapsedAt?: string): string {
  if (n.disabledReason === 'plan-lapsed') return `Off the mesh since the team left Teams${lapsedAt && !Number.isNaN(Date.parse(lapsedAt)) ? ` on ${longDay(lapsedAt)}` : ''}`;
  return n.disabledReason ? `Off the mesh: ${OFF_REASON[n.disabledReason]}` : 'Off the mesh';
}

/**
 * What the relays allow the organisation, the same on every relay, in words: "hold 100 sessions at
 * once and carry 50 MB a minute" (binary units, as the workbench shows them). Nothing when nothing is limited.
 */
export function relayAllowanceInWords(l: MeshRelayLimits | undefined): string | undefined {
  if (!l) return undefined;
  const parts: string[] = [];
  if (l.sessions !== null) parts.push(`hold ${l.sessions.toLocaleString()} ${l.sessions === 1 ? 'session' : 'sessions'} at once`);
  if (l.bytesPerMinute !== null) parts.push(`carry ${bytesInWords(l.bytesPerMinute)} a minute`);
  return parts.length ? parts.join(' and ') : undefined;
}

/** A machine's mesh node: the one linked to its device id, else the one holding the key the device view names. */
export function meshNodeOf(device: Pick<FleetDevice, 'deviceId' | 'meshPublicKey'> | undefined, nodes: readonly MeshNode[] | undefined): MeshNode | undefined {
  if (!device || !nodes) return undefined;
  return nodes.find((n) => n.deviceId === device.deviceId) ?? (device.meshPublicKey ? nodes.find((n) => n.publicKey === device.meshPublicKey) : undefined);
}

/** A machine's place on the mesh in one line: on it as its name and address, off it and why, or not on it. */
export function meshLineOf(device: Pick<FleetDevice, 'deviceId' | 'meshPublicKey'> | undefined, nodes: readonly MeshNode[] | undefined): string {
  const node = meshNodeOf(device, nodes);
  if (node) return node.disabled ? offTheMeshInWords(node) : `On the mesh as ${node.dnsName} · ${node.address}`;
  // The device view names a key whose node this person cannot list (someone else's): on the mesh, no more said.
  return device?.meshPublicKey ? 'On the mesh' : 'Not on the mesh';
}

/** Bytes in words: 512 B, 12 KB, 2.4 MB, 1.1 GB. */
export function bytesInWords(n: number | undefined): string {
  if (n === undefined) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${i === 0 ? v : v >= 100 ? Math.round(v) : v.toFixed(1).replace(/\.0$/, '')} ${units[i]}`;
}

/** "patched today", "patched 3 days ago". */
function patchedInWords(iso: string, now: number): string | undefined {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  const days = Math.max(0, Math.floor((now - t) / DAY));
  return days === 0 ? 'patched today' : `patched ${days} ${days === 1 ? 'day' : 'days'} ago`;
}

/** The machine's posture in words: "disk encrypted, firewall on, patched 3 days ago", or "posture unknown". */
export function postureInWords(p: DevicePosture | undefined, now = Date.now()): string {
  if (!p) return 'posture unknown';
  const parts: string[] = [];
  if (p.diskEncrypted !== undefined) parts.push(p.diskEncrypted ? 'disk encrypted' : 'disk not encrypted');
  if (p.firewall !== undefined) parts.push(p.firewall ? 'firewall on' : 'firewall off');
  if (p.screenLock !== undefined) parts.push(p.screenLock ? 'screen lock on' : 'no screen lock');
  if (p.patchedAt) {
    const w = patchedInWords(p.patchedAt, now);
    if (w) parts.push(w);
  }
  return parts.length ? parts.join(', ') : 'posture unknown';
}

/** True when the posture says something is off (unencrypted, firewall off, no screen lock). */
export function postureWeak(p: DevicePosture | undefined): boolean {
  return !!p && (p.diskEncrypted === false || p.firewall === false || p.screenLock === false);
}

const OS_LABEL: Record<string, string> = { windows: 'Windows', darwin: 'macOS', linux: 'Linux' };

/** What a grant requires of the source machine, in words. */
export function requirementsInWords(r: MeshPostureRequirement | undefined): string[] {
  if (!r) return [];
  const out: string[] = [];
  if (r.diskEncrypted) out.push('disk encryption');
  if (r.firewall) out.push('firewall on');
  if (r.screenLock) out.push('screen lock');
  if (r.patchedWithinDays) out.push(`patched within ${r.patchedWithinDays} ${r.patchedWithinDays === 1 ? 'day' : 'days'}`);
  for (const [os, v] of Object.entries(r.minOsVersion ?? {})) if (v) out.push(`${OS_LABEL[os] ?? os} ${v} or later`);
  return out;
}
